import React, { Component } from 'react'
import { connect } from 'react-redux'
import { HorizontalBar } from 'react-chartjs-2'
import { injectIntl } from 'react-intl'
import WidgetContainer from 'components/Apps/WidgetContainer'
import {
  fetchSiteDLFData,
  fetchSiloDLFData,
  resetSiloDLFData,
} from 'reducers/slaMonitor/reducer'
import {
  DEFAULT_CHART_COLOR_RGBA,
  DEFAULT_CHART_COLOR_RGBA_HOVER,
} from 'constants/insight'
import messages from 'components/Apps/SlaMonitor/translate'
import { mathRound, getFormattedNum } from 'helpers/math'
import { getChartsRatio } from 'helpers/slaMonitor'
import ifWindowWidthLessThan from 'helpers/ifWindowWidthLessThan'
import CircleLoader from 'components/Loading/CircleLoader'

class SiteDaysLeftFeeding extends Component {
  constructor(props) {
    super(props)
    this.state = {
      barIndex: 0,
    }
  }

  getChartParams = () => {
    const siloList = this.props.siteDLFData.get('data')
    const { barIndex } = this.state
    const labels = []
    const data = []
    const backgroundColors = []
    const xTicks = { max: 0 }

    for (const key in siloList) {
      if (Object.prototype.hasOwnProperty.call(siloList, key)) {
        const silo = siloList[key]
        labels.push(silo.title)
        data.push([
          0,
          silo.days_left === 'N/A' || silo.days_left < 0 ? 0 : silo.days_left,
          silo.days_left,
          mathRound(silo.remaining_feed),
          mathRound(silo.avg_daily_feeding),
          silo.diet,
        ])
        backgroundColors.push(
          barIndex === +key
            ? DEFAULT_CHART_COLOR_RGBA
            : DEFAULT_CHART_COLOR_RGBA_HOVER,
        )

        if (silo.days_left > xTicks.max) {
          xTicks.max = silo.days_left
        }
      }
    }

    xTicks.max = Math.ceil((xTicks.max + xTicks.max * 0.4) / 10) * 10

    return { labels, backgroundColors, data, xTicks }
  }

  chartData = ({ labels, backgroundColors, data }) => {
    const {
      intl: { formatMessage },
    } = this.props

    return {
      labels,
      datasets: [
        {
          label: `${formatMessage(messages.feeding)} (kg)`,
          fill: false,
          backgroundColor: backgroundColors,
          pointRadius: 3,
          pointHitRadius: 3,
          pointHoverRadius: 3,
          data,
        },
      ],
    }
  }

  chartOptions = ({ xTicks }) => {
    const {
      intl: { formatMessage, formatNumber },
    } = this.props

    return {
      aspectRatio: getChartsRatio(),
      responsive: true,
      legend: {
        display: false,
      },
      scales: {
        xAxes: [
          {
            stacked: true,
            ticks: xTicks,
            scaleLabel: {
              display: true,
              labelString: `${formatMessage(messages.daysRemaining)}`,
            },
          },
        ],
        yAxes: [
          {
            stacked: true,
            ticks: {},
            scaleLabel: {
              display: true,
              labelString: `${formatMessage(messages.silos)}`,
            },
          },
        ],
      },
      tooltips: {
        enabled: true,
        mode: 'single',
        intersect: true,
        backgroundColor: '#03254a',
        animationDuration: 300,
        titleFontSize: 13,
        bodyFontSize: 10,
        caretSize: 10,
        displayColors: false,
        callbacks: {
          title: e => `${e[0].label}`,
          label: (tooltipItem, data) => {
            const dataset = data.datasets[tooltipItem.datasetIndex]
            const item = dataset.data[tooltipItem.index]
            const daysLeftFeeding = Array.isArray(item) ? item[2] : item
            const remainingFeeding = Array.isArray(item) ? item[3] : item
            const diet = Array.isArray(item) && item[5] ? item[5] : 'N/A'

            return [
              `${formatMessage(messages.daysLeftFeeding)}: ${formatNumber(
                daysLeftFeeding,
              )}`,
              `${formatMessage(messages.remainingFeed)}: ${formatNumber(
                remainingFeeding,
              )} kg`,
              `${formatMessage(messages.diet)}: ${diet}`,
            ]
          },
        },
      },
      plugins: {
        datalabels: {
          anchor: 'end',
          align: 'end',
          color: 'black',
          font: {
            weight: 'bold',
            size: ifWindowWidthLessThan(1280) ? 9 : 11,
          },
          listeners: {
            click: this.onBarLabelClick,
          },
          formatter: v => `${v[2]} (${formatNumber(v[3])})`,
        },
      },
    }
  }

  onBarLabelClick = chartEvent => {
    this.setHorizontalBarClick(chartEvent.dataIndex)
  }

  onHorizontalBarClick = chartEvent => {
    if (chartEvent.length) {
      this.setHorizontalBarClick(chartEvent[0]._index)
    }
  }

  setHorizontalBarClick = barIndex => {
    const { siteDLFData, activeDate, goToCage } = this.props
    const silo = siteDLFData.get('data')[barIndex]

    this.setState({ barIndex }, () => {
      this.fetchSiloDLFData(silo.id, activeDate)

      if (ifWindowWidthLessThan(992)) {
        goToCage()
      }
    })
  }

  fetchSiteDLFData = (siteId, activeDate) => {
    this.props.fetchSiteDLFData({
      site: siteId,
      date: activeDate.format('YYYY-MM-DD'),
    })
  }

  fetchSiloDLFData = (cageId, activeDate) => {
    this.props.fetchSiloDLFData({
      silo: cageId,
      date: activeDate.format('YYYY-MM-DD'),
    })
  }

  componentDidMount() {
    const { activeSite, activeDate } = this.props
    this.fetchSiteDLFData(activeSite.id, activeDate)
  }

  shouldComponentUpdate(nextProps) {
    const { activeSite, activeDate, siteDLFData, resetSiloDLFData } = this.props

    if (
      activeSite !== nextProps.activeSite ||
      activeDate !== nextProps.activeDate
    ) {
      this.setState(
        {
          barIndex: 0,
        },
        () => {
          this.fetchSiteDLFData(nextProps.activeSite.id, nextProps.activeDate)
        },
      )

      return false
    }

    if (siteDLFData !== nextProps.siteDLFData) {
      const siloList = nextProps.siteDLFData.get('data')
      if (siloList.length) {
        this.fetchSiloDLFData(siloList[0].id, nextProps.activeDate)
      } else {
        resetSiloDLFData()
      }
    }

    return true
  }

  render() {
    const {
      hideRender,
      siteDLFData,
      chartLoader,
      activeSite,
      intl: { formatMessage },
    } = this.props

    if (hideRender === false) {
      return null
    }

    const chartParams = this.getChartParams()

    return (
      <WidgetContainer
        name={{
          start: formatMessage(messages.daysLeftFeeding),
          end: activeSite.name,
        }}
        sideTitle={siteDLFData.get('last_updated')}
        className="feed-module">
        <div className="feed-chart-box">
          <div className="chart-info">
            <div className="info-item">
              <span className="item-count">
                {getFormattedNum(mathRound(siteDLFData.get('remaining_feed')))}
              </span>
              <span className="item-name">
                {formatMessage(messages.remaining)} (kg)
              </span>
            </div>
            <div className="info-item">
              <span className="item-count">
                {getFormattedNum(
                  mathRound(siteDLFData.get('avg_daily_feeding')),
                )}
              </span>
              <span className="item-name">
                {formatMessage(messages.avgDaily)} (kg)
              </span>
            </div>
            <div className="info-item">
              <span className="item-count">{siteDLFData.get('days_left')}</span>
              <span className="item-name">
                {formatMessage(messages.daysLeft)}
              </span>
            </div>
          </div>
          <div className="chart-content">
            <CircleLoader loading={chartLoader} />
            <HorizontalBar
              width={null}
              height={null}
              data={this.chartData(chartParams)}
              options={this.chartOptions(chartParams)}
              getElementAtEvent={this.onHorizontalBarClick}
            />
          </div>
        </div>
      </WidgetContainer>
    )
  }
}

export default connect(
  state => ({
    siteDLFData: state.getIn(['slaMonitor', 'siteDLFData']),
    activeSite: state.getIn(['profile', 'profile', 'site']),
    activeDate: state.getIn(['slaMonitor', 'activeDate']),
    chartLoader: state.getIn(['slaMonitor', 'siteDLFLoader']),
  }),
  {
    fetchSiteDLFData,
    fetchSiloDLFData,
    resetSiloDLFData,
  },
)(injectIntl(SiteDaysLeftFeeding))
